/**
 * The live open-role portfolio for Role Prioritisation.
 *
 * The proxy holds the Airtable key; the browser never sees it. A failure here is not an exception
 * to swallow — the tab has a seeded baseline to fall back to, and which of the two is on screen has
 * to be visible before anyone commits capacity against it. So the route answers 200 with
 * `source: 'unavailable'` plus a reason, and a transport failure is turned into the same shape.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { LivePortfolio } from '../types/rolePrioritisation';

// Matches the deployed Cloud Run service name (`proxy-prod-people`), as every other service in
// this folder does. CLAUDE.md's illustrative snippet names a different host — do not copy it.
const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export async function fetchLivePortfolio(fresh = false): Promise<LivePortfolio> {
  const url = `${PROXY_URL}/api/ta-cockpit/role-prioritisation${fresh ? '?fresh=1' : ''}`;
  try {
    const res = await authenticatedFetch(url);
    if (!res.ok) {
      return {
        source: 'unavailable',
        reason: `The cockpit proxy answered ${res.status}`,
        syncedAt: new Date().toISOString(),
      };
    }
    return (await res.json()) as LivePortfolio;
  } catch (err) {
    return {
      source: 'unavailable',
      reason: err instanceof Error ? err.message : String(err),
      syncedAt: new Date().toISOString(),
    };
  }
}
